import { computed } from 'vue';
import store from '@/store';

export const FALLBACK_CLUB_COLOR = '#2563eb';
export const CLUB_COLOR_DARK_TEXT = '#0b1c30';
export const WCAG_AA_NORMAL_TEXT_CONTRAST = 4.5;

const HEX_COLOR_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Validate a CSS hex color (#abc or #aabbcc).
 * @param {*} value
 * @returns {boolean}
 */
export function isValidHexColor(value) {
    return typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim());
}

/**
 * Normalize a hex color to lowercase 6-digit form (#abc -> #aabbcc).
 * @param {string} value
 * @returns {string}
 */
export function normalizeHexColor(value) {
    const hex = String(value).trim().toLowerCase();
    if (hex.length === 4) {
        return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex;
}

/**
 * WCAG relative luminance of a hex color.
 * @param {string} value
 * @returns {number} 0..1
 */
export function relativeLuminance(value) {
    const hex = normalizeHexColor(value);
    const channels = [1, 3, 5].map(offset => {
        const srgb = parseInt(hex.slice(offset, offset + 2), 16) / 255;
        return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/**
 * WCAG contrast ratio between two relative luminances.
 * @param {number} luminanceA
 * @param {number} luminanceB
 * @returns {number}
 */
export function contrastRatio(luminanceA, luminanceB) {
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the readable text color (white or dark slate) on top of a hex background.
 * Guarantees WCAG AA for normal text (>= 4.5:1). Mid-tone backgrounds where
 * neither brand candidate reaches the threshold fall back to pure black/white,
 * which always has at least one candidate above 4.5:1.
 * @param {string} backgroundHex
 * @returns {string}
 */
export function readableTextColor(backgroundHex) {
    const background = relativeLuminance(backgroundHex);
    const whiteContrast = contrastRatio(background, 1);
    const darkContrast = contrastRatio(background, relativeLuminance(CLUB_COLOR_DARK_TEXT));

    if (whiteContrast >= WCAG_AA_NORMAL_TEXT_CONTRAST && whiteContrast >= darkContrast) {
        return '#ffffff';
    }
    if (darkContrast >= WCAG_AA_NORMAL_TEXT_CONTRAST) {
        return CLUB_COLOR_DARK_TEXT;
    }

    const blackContrast = contrastRatio(background, 0);
    return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
}

/**
 * Club identity color from the manager settings (the same value AppLoader
 * exposes as `--sitecolor`), with an invalid/missing fallback and a WCAG
 * contrast guard for text placed on top of it.
 */
export function useAdminClubIdentity() {
    const clubColor = computed(() => {
        const raw = store.getters.getFooter && store.getters.getFooter.sitecolor;
        return isValidHexColor(raw) ? normalizeHexColor(raw) : FALLBACK_CLUB_COLOR;
    });

    const clubTextColor = computed(() => readableTextColor(clubColor.value));

    return { clubColor, clubTextColor };
}
